import { useQuery, useSuspenseQuery, UseQueryOptions } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { WorkflowStateEnum, WorkflowTypeEnum } from '@omnea-app/packages/universal/workflow/workflowEnums'
import { WorkflowsHttpClient, WorkflowsQuery, WorkflowsResponse } from '@omnea-app/workflows/workflowsHttpClient'
import { workflowQueryKeys } from './queryKeys'

type WorkflowsQueryParams = WorkflowsQuery<'/', 'get'>
type WorkflowState = NonNullable<WorkflowsQueryParams['state']>[number]
type WorkflowType = NonNullable<WorkflowsQueryParams['type']>[number]

type WorkflowsApiResponse = WorkflowsResponse<'/', 'get'>
type WorkflowsApiData = NonNullable<WorkflowsApiResponse['data']>
type WorkflowItem = NonNullable<WorkflowsApiData['workflows']>[number]

export type WorkflowsQueryData = {
  workflows: Array<WorkflowItem & { isArchived: boolean }>
}

interface WorkflowQueryOptions<TData = WorkflowsQueryData> {
  skip?: boolean
  includeArchived?: boolean
  state?: WorkflowState[]
  workflowType?: WorkflowType
  workflowTypes?: WorkflowType[]
  excludeHiddenFromRequestors?: boolean
  select?: (data: WorkflowsQueryData) => TData
}

interface CreateWorkflowsQueryOptionsParams<TData = WorkflowsQueryData> extends WorkflowQueryOptions<TData> {
  getToken: () => Promise<string>
}

const createWorkflowsQueryOptions = <TData = WorkflowsQueryData>({
  getToken,
  includeArchived = true,
  skip = false,
  state,
  workflowType,
  workflowTypes,
  excludeHiddenFromRequestors,
  select,
}: CreateWorkflowsQueryOptionsParams<TData>): UseQueryOptions<WorkflowsQueryData, Error, TData> & {
  queryFn: () => Promise<WorkflowsQueryData>
} => ({
  queryKey: workflowQueryKeys.list({ includeArchived, state, workflowType, workflowTypes, excludeHiddenFromRequestors }),
  queryFn: async () => {
    const token = await getToken()
    const apiClient = new WorkflowsHttpClient({ authorization: token })

    const types: WorkflowsQueryParams['type'] =
      (workflowTypes as WorkflowsQueryParams['type']) ??
      (isDefined(workflowType) ? ([workflowType] as WorkflowsQueryParams['type']) : undefined)

    const queryParams: WorkflowsQueryParams = {
      state,
      type: types,
      excludeHiddenFromRequestors,
    }

    if (includeArchived) {
      const [nonArchivedResult, archivedResult] = await Promise.all([
        apiClient.GET('/', {
          params: { query: { ...queryParams, isArchived: false } },
        }),
        apiClient.GET('/', {
          params: { query: { ...queryParams, isArchived: true } },
        }),
      ])

      throwIfApiError(nonArchivedResult.error)
      throwIfApiError(archivedResult.error)

      return {
        workflows: [
          ...(nonArchivedResult.data?.workflows.map((workflow) => ({ ...workflow, isArchived: false })) ?? []),
          ...(archivedResult.data?.workflows.map((workflow) => ({ ...workflow, isArchived: true })) ?? []),
        ],
      }
    } else {
      const { data, error } = await apiClient.GET('/', {
        params: { query: { ...queryParams, isArchived: false } },
      })

      throwIfApiError(error)

      return {
        workflows: data?.workflows.map((workflow) => ({ ...workflow, isArchived: false })) ?? [],
      }
    }
  },
  enabled: !skip,
  select,
})

export const useListWorkflowsSuspense = <TData = WorkflowsQueryData>(options?: WorkflowQueryOptions<TData>) => {
  const { getToken } = useAuthenticatedUser()

  const queryOptions = createWorkflowsQueryOptions<TData>({ ...options, getToken })
  const result = useSuspenseQuery(queryOptions)
  return result.data
}

export const useListWorkflows = <TData = WorkflowsQueryData>(options?: WorkflowQueryOptions<TData>) => {
  const { getToken } = useAuthenticatedUser()

  const queryOptions = createWorkflowsQueryOptions<TData>({ ...options, getToken })
  return useQuery(queryOptions)
}

/**
 * Select function to transform and sort workflows for the workflows list page
 */
export const selectWorkflowsList = (data: WorkflowsQueryData) =>
  data.workflows
    .map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description ?? '',
      state: item.state as WorkflowStateEnum,
      type: item.type as WorkflowTypeEnum,
      url: item.url ?? null,
      isArchived: item.isArchived,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      updatedBy: {
        firstName: item.updated_by_user?.firstName ?? undefined,
        lastName: item.updated_by_user?.lastName ?? undefined,
      },
      rank: item.workflow_rank?.rank ?? null,
      viewModelUpdatedAt: item.viewModelUpdatedAt ?? null,
      draftId: item.draftId,
    }))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
